<template>
  <div>
    <!-- Header -->
    <div class="card-filter-section mt-0 d-flex align-items-center justify-content-between">
      <p class="task-timesheet m-0">
        Shift Management
      </p>
      <div class="role-buttons d-flex flex-wrap align-items-center">
        <table-button
          btn-name="Add Shift"
          @click.native="openAddShift"
        />
        <Help
          v-if="$store.state.app.userManualList"
          code="SM"
        />
      </div>
    </div>

    <!-- Filters -->
    <div class="card-filter-section">
      <div class="d-flex align-items-center flex-wrap justify-content-between">
        <div class="d-flex align-items-center justify-content-start flex-wrap">
          <p class="filter-title">
            Filters:
          </p>
          <div>
            <custom-dropdown
              v-model="is_active"
              :options="isActiveList"
              :search-box="false"
              :hide-close="true"
              label="Active"
              icon="EditIcon"
            />
          </div>
          <option-drop-down v-model="perPage" />
        </div>

        <div class="d-flex align-items-center">
          <b-input-group
            class="input-group-merge search-project"
            style="min-width: 250px; max-width: 250px; width: 250px"
          >
            <b-form-input
              v-model="debouncedSearch"
              placeholder="Search by Shift Name"
            />
            <b-input-group-append is-text>
              <feather-icon
                v-if="debouncedSearch"
                icon="XIcon"
                @click="debouncedSearch = null; search = null"
              />
              <feather-icon icon="SearchIcon" />
            </b-input-group-append>
          </b-input-group>

          <div class="ml-1">
            <button
              class="apply-filter filterRedesign mr-1"
              @click="getShiftList()"
            >
              Apply Filter
            </button>
            <button
              class="apply-filter-clear filterRedesign"
              @click="clearFilter"
            >
              Clear
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="horizontal-line table">
      <table-loader
        v-if="loader"
        :fields="fields"
      />
      <b-row v-else>
        <b-col cols="12">
          <b-table
            responsive
            :items="shifts"
            :fields="fields"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            show-empty
            class="scrollable-element user-table-items tableDesigns mastertblColor shift-table"
          >
            <!-- Shift Name -->
            <template #cell(name)="data">
              <span class="font-weight-bold">{{ data.item.name }}</span>
            </template>

            <!-- Shift Code -->
            <template #cell(code)="data">
              <span class="shift-code-chip">{{ data.item.code }}</span>
            </template>

            <!-- Start Time -->
            <template #cell(start_time)="data">
              <span class="time-value">{{ data.item.start_time | fullTimeFormat }}</span>
            </template>

            <!-- End Time -->
            <template #cell(end_time)="data">
              <span class="time-value">{{ data.item.end_time | fullTimeFormat }}</span>
            </template>

            <!-- Working Hours -->
            <template #cell(total_working_hours)="data">
              <span class="working-hours-chip">
                <feather-icon
                  icon="ClockIcon"
                  size="12"
                  class="mr-25"
                />
                {{ data.item.total_working_hours != null ? data.item.total_working_hours + 'h' : '-' }}
              </span>
            </template>

            <!-- Working Days -->
            <template #cell(working_days)="data">
              <weekday-selector
                :value="data.item.working_days || []"
                readonly
                sm
                active-color="#2178fb"
                shadow-color="rgba(33,120,251,0.35)"
              />
            </template>

            <!-- Weekly Off Days -->
            <template #cell(weekly_off_days)="data">
              <weekday-selector
                :value="data.item.weekly_off_days || []"
                readonly
                sm
                active-color="#ff9f43"
                shadow-color="rgba(255,159,67,0.35)"
              />
            </template>

            <!-- Grace Minutes -->
            <template #cell(grace_minutes)="data">
              <span class="grace-chip">{{ data.item.grace_minutes || 0 }} min</span>
            </template>

            <!-- Lunch Break -->
            <template #cell(lunch_break_minutes)="data">
              <span class="grace-chip">{{ data.item.lunch_break_minutes || 0 }} min</span>
            </template>

            <!-- Status — toggle switch (same as Tag / Employee modules) -->
            <template #cell(is_active)="data">
              <b-form-checkbox
                :checked="isShiftActive(data.item)"
                class="custom-control-success"
                name="check-button"
                size="md"
                switch
                @change="changeStatus(data.item)"
              >
                <span class="switch-icon-right">
                  <feather-icon icon="XIcon" />
                </span>
                <span class="switch-icon-left">
                  <feather-icon icon="CheckIcon" />
                </span>
              </b-form-checkbox>
            </template>

            <!-- Created By -->
            <template #cell(created_by)="data">
              <div
                v-if="data.item.created_by"
                class="d-flex align-items-center"
              >
                <b-avatar
                  size="30"
                  :text="data.item.created_by.full_name | avatarText"
                  :src="data.item.created_by.picture || null"
                  style="background-color: #f97753"
                  class="mr-75"
                />
                <span class="creator-name">{{ data.item.created_by.full_name }}</span>
              </div>
              <span v-else>-</span>
            </template>

            <!-- Actions -->
            <template #cell(action)="data">
              <span class="d-flex align-items-center justify-content-center">
                <feather-icon
                  v-b-tooltip.hover.top="'View'"
                  icon="EyeIcon"
                  class="mr-1 text-primary cursor-pointer"
                  size="18"
                  @click="openViewShift(data.item)"
                />
                <feather-icon
                  v-b-tooltip.hover.top="'Edit'"
                  icon="EditIcon"
                  class="text-dark cursor-pointer"
                  size="18"
                  @click="openEditShift(data.item)"
                />
              </span>
            </template>
          </b-table>

          <img
            v-if="totalCount === 0"
            src="../../assets/images/nodatafound/nodata.svg"
            alt="no-data"
            class="no-data-img"
          >
        </b-col>
      </b-row>

      <!-- Pagination -->
      <Pagination
        v-if="totalCount > 0 && !loader"
        :current-page="currentPage"
        :per-page="perPage"
        :total-count="totalCount"
        @currentPage="currentPage = $event"
      />

      <!-- Add / Edit Shift Sidebar -->
      <add-edit-shift
        ref="addEditShift"
        @shiftSaved="getShiftList"
      />

      <!-- View Shift Sidebar -->
      <view-shift ref="viewShift" @editShift="openEditShift" />
    </div>
  </div>
</template>

<script>
import ShiftMixin from '@/mixin/masterSetting/Shift/index'
import WeekdaySelector from '@/components/weekdaySelector/WeekdaySelector.vue'

export default {
  components: { WeekdaySelector },
  mixins: [ShiftMixin],
}
</script>

<style lang="scss" scoped>
@import "./../../assets/scss/component-css/masterSettingFilter.scss";
</style>
